/**
 * 百度地图服务类
 * 替代高德地图服务 amap.js，提供相同的接口
 */
import { loadBMapGL, BAIDU_MAP_AK } from './bmap-loader';

// 导出常量，保持与高德地图相同的命名约定
export const LOCATION_KEY = BAIDU_MAP_AK;
export const BMAP_CONTAINER_ID = 'bmap-container';
// 兼容旧代码的别名
export const AMAP_CONTAINER_ID = BMAP_CONTAINER_ID;

// 百度地图状态码常量（避免直接使用全局变量导致编译报错）
// BMAP_STATUS_SUCCESS = 0 表示成功
const STATUS_SUCCESS = 0;

class BMapService {
  BMapGL = null; // 百度地图类
  map = null; // 地图实例
  localSearch = null; // 搜索实例（对应高德的PlaceSearch）
  geolocation = null; // 定位实例
  currentLocation = null; // 当前位置

  /**
   * 初始化百度地图
   */
  init() {
    loadBMapGL()
      .then(BMapGL => {
        this.BMapGL = BMapGL;
        this.initMap(BMapGL);
        this.initLocalSearch();
        this.initGeolocation();
      })
      .catch(err => {
        console.error('百度地图加载失败:', err);
      });
  }

  /**
   * 初始化地图实例
   * @param {Object} BMapGL - 百度地图GL对象
   */
  initMap(BMapGL) {
    const map = new BMapGL.Map(BMAP_CONTAINER_ID, {
      enableMapClick: true
    });
    // 初始化地图，设置中心点和缩放级别
    map.centerAndZoom(new BMapGL.Point(116.404, 39.915), 12);
    map.enableScrollWheelZoom(true);
    this.map = map;
  }

  /**
   * 初始化本地搜索（对应高德的PlaceSearch）
   */
  initLocalSearch() {
    const localSearch = new this.BMapGL.LocalSearch(this.map, {
      pageCapacity: 10, // 单页显示结果条数（对应高德的pageSize）
      onSearchComplete: null // 搜索完成回调，将在search方法中动态设置
    });
    this.localSearch = localSearch;
  }

  /**
   * 初始化定位服务
   */
  initGeolocation() {
    const geolocation = new this.BMapGL.Geolocation();
    // 开启SDK辅助定位，提高定位精度
    geolocation.enableSDKLocation();
    this.geolocation = geolocation;
    this.getCurrentPosition();
  }

  /**
   * 获取当前位置
   * 转换百度地图返回格式为高德地图格式，保持接口一致性
   */
  getCurrentPosition() {
    this.geolocation.getCurrentPosition((result) => {
      if (this.geolocation.getStatus() === STATUS_SUCCESS) {
        console.log('定位成功', result);

        // 使用逆地理编码获取详细地址信息
        const geocoder = new this.BMapGL.Geocoder();
        geocoder.getLocation(result.point, (rs) => {
          if (rs) {
            // 转换为与高德地图相同的数据结构
            this.currentLocation = {
              position: result.point,
              accuracy: result.accuracy,
              formattedAddress: rs.address,
              addressComponent: {
                province: rs.addressComponents.province,
                city: rs.addressComponents.city,
                district: rs.addressComponents.district,
                street: rs.addressComponents.street,
                streetNumber: rs.addressComponents.streetNumber
              }
            };
            this.resetLocalSearchCity();
          }
        });
      } else {
        console.log('定位失败', this.geolocation.getStatus());
        this.currentLocation = null;
      }
    });
  }

  /**
   * 重置搜索城市（对应高德的resetPlacesearchCity）
   */
  resetPlacesearchCity() {
    this.resetLocalSearchCity();
  }

  /**
   * 重置搜索城市
   */
  resetLocalSearchCity() {
    if (this.localSearch && this.currentLocation) {
      this.localSearch.setLocation(this.currentLocation.addressComponent.city);
    }
  }

  /**
   * 关键词搜索
   * @param {string} keywords - 搜索关键词
   * @param {Function} callback - 回调函数，格式与高德保持一致 (status, result)
   */
  search(keywords, callback) {
    if (!this.localSearch) return;

    const self = this;
    this.localSearch.setSearchCompleteCallback((results) => {
      if (self.localSearch.getStatus() === STATUS_SUCCESS) {
        // 转换为高德地图的返回格式
        const pois = [];
        for (let i = 0; i < results.getCurrentNumPois(); i++) {
          const poi = results.getPoi(i);
          pois.push(self._convertPoiFormat(poi));
        }
        // 保持与高德地图回调格式一致：(status, result)
        /* eslint-disable-next-line standard/no-callback-literal */
        callback('complete', { poiList: { pois: pois } });
      } else {
        /* eslint-disable-next-line standard/no-callback-literal */
        callback('error', null);
      }
    });

    this.localSearch.search(keywords);
  }

  /**
   * 周边搜索（对应高德的searchNearBy）
   * @param {Function} callback - 回调函数
   */
  searchNearby(callback) {
    if (!this.localSearch || !this.currentLocation) return;

    const self = this;
    this.localSearch.setSearchCompleteCallback((results) => {
      if (self.localSearch.getStatus() === STATUS_SUCCESS) {
        // 转换为高德地图的返回格式
        const pois = [];
        const numPois = results.getCurrentNumPois();
        for (let i = 0; i < numPois; i++) {
          const poi = results.getPoi(i);
          pois.push(self._convertPoiFormat(poi));
        }
        // 保持与高德地图回调格式一致：(status, result)
        /* eslint-disable-next-line standard/no-callback-literal */
        callback('complete', { poiList: { pois: pois } });
      } else {
        /* eslint-disable-next-line standard/no-callback-literal */
        callback('error', null);
      }
    });

    // 周边搜索：关键词、中心点、半径
    this.localSearch.searchNearby('公司企业', this.currentLocation.position, 1000);
  }

  /**
   * 转换百度POI格式为高德POI格式
   * @param {Object} baiduPoi - 百度POI对象
   * @returns {Object} 高德格式的POI对象
   */
  _convertPoiFormat(baiduPoi) {
    return {
      id: baiduPoi.uid || `poi_${Date.now()}_${Math.random()}`,
      name: baiduPoi.title || '',
      address: baiduPoi.address || '',
      location: baiduPoi.point,
      // 百度 POI 可能没有直接的省市区信息，使用空字符串作为默认值
      pname: baiduPoi.province || '',
      cityname: baiduPoi.city || '',
      adname: baiduPoi.district || '',
      // 保留原始百度POI数据
      _raw: baiduPoi
    };
  }

  /**
   * 通过逆地理编码补充 POI 的省市区信息
   * @param {Object} poi - POI对象
   * @returns {Promise<Object>} 补充了省市区信息的POI对象
   */
  enrichPoiWithAddress(poi) {
    return new Promise((resolve) => {
      const point = poi.location || poi.point;
      if (!point) {
        resolve(poi);
        return;
      }

      const geocoder = new this.BMapGL.Geocoder();
      geocoder.getLocation(point, (rs) => {
        if (rs && rs.addressComponents) {
          const addr = rs.addressComponents;
          const province = addr.province || '';
          const city = addr.city || '';
          const district = addr.district || '';

          // 从 address 中去除省市区前缀，只保留街道及更精细的地址
          let cleanAddress = poi.address || '';
          // 依次去除省、市、区前缀
          if (province && cleanAddress.startsWith(province)) {
            cleanAddress = cleanAddress.substring(province.length);
          }
          if (city && cleanAddress.startsWith(city)) {
            cleanAddress = cleanAddress.substring(city.length);
          }
          if (district && cleanAddress.startsWith(district)) {
            cleanAddress = cleanAddress.substring(district.length);
          }

          resolve({
            ...poi,
            address: cleanAddress, // 使用去除省市区后的地址
            pname: province,
            cityname: city,
            adname: district
          });
        } else {
          resolve(poi);
        }
      });
    });
  }

  // 当前显示的自定义覆盖物
  currentOverlay = null;

  /**
   * 聚焦到某个POI点（对应高德的focusPoi）
   * @param {Object} poi - POI对象
   * @param {Function} cb - 回调函数
   */
  focusPoi(poi, cb) {
    const point = poi.location || poi.point;
    this.map.centerAndZoom(point, 18);
    // 使用自定义覆盖物替代 InfoWindow，完全自定义样式
    this.showCustomOverlay(poi.name, point);
  }

  /**
   * 显示自定义覆盖物（无默认样式的信息窗口）
   * @param {string} content - 显示内容
   * @param {Object} point - 坐标点
   */
  showCustomOverlay(content, point) {
    // 移除之前的覆盖物
    if (this.currentOverlay) {
      this.map.removeOverlay(this.currentOverlay);
    }

    const self = this;
    // 创建自定义 DOM 元素
    function createDOM() {
      const div = document.createElement('div');
      div.className = 'custom-info-window';
      div.innerHTML = `<span>${content}</span>`;
      return div;
    }

    // 创建自定义覆盖物
    const customOverlay = new this.BMapGL.CustomOverlay(createDOM, {
      point: point,
      offsetY: -50, // 向上偏移，避免遮挡标记点
      map: self.map
    });

    this.map.addOverlay(customOverlay);
    this.currentOverlay = customOverlay;
  }

  /**
   * 平移地图（对应高德的panPoiToCenter）
   * @param {number} x - X方向偏移像素
   * @param {number} y - Y方向偏移像素
   */
  panPoiToCenter(x, y) {
    this.map.panBy(x, y);
  }

  /**
   * 创建信息窗口（对应高德的createInfoWindow）
   * 保留此方法用于兼容，但推荐使用 showCustomOverlay
   * @param {string} content - 窗口内容
   * @returns {Object} 信息窗口对象
   */
  createInfoWindow(content) {
    const infoWindow = new this.BMapGL.InfoWindow(
      `<div class='custom-info-window'><span>${content}</span></div>`,
      {
        offset: new this.BMapGL.Size(0, -45)
      }
    );
    return infoWindow;
  }

  /**
   * 兼容高德的placeSearch属性访问
   * 返回一个兼容对象
   */
  get placeSearch() {
    const self = this;
    return {
      setCity: (city) => {
        if (self.localSearch) {
          self.localSearch.setLocation(city);
        }
      },
      setPageSize: (size) => {
        if (self.localSearch) {
          self.localSearch.setPageCapacity(size);
        }
      },
      search: (keywords, callback) => {
        self.search(keywords, callback);
      },
      searchNearBy: (keyword, center, radius, callback) => {
        if (!self.localSearch) return;

        self.localSearch.setSearchCompleteCallback((results) => {
          if (self.localSearch.getStatus() === STATUS_SUCCESS) {
            const pois = [];
            for (let i = 0; i < results.getCurrentNumPois(); i++) {
              const poi = results.getPoi(i);
              pois.push(self._convertPoiFormat(poi));
            }
            // 保持与高德地图回调格式一致：(status, result)
            /* eslint-disable-next-line standard/no-callback-literal */
            callback('complete', { poiList: { pois: pois } });
          } else {
            /* eslint-disable-next-line standard/no-callback-literal */
            callback('error', null);
          }
        });

        self.localSearch.searchNearby(keyword, center, radius);
      }
    };
  }
}

const bmapService = new BMapService();

export default bmapService;



// WEBPACK FOOTER //
// ./src/stores/location/bmap.js